import { J2Multiply, J2Validate } from '@e3dc-react/icons'
import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { Box, Button, Typography } from '@mui/material'
import { format } from 'date-fns'
import React from 'react'
import useModals from 'src/Hooks/useModals'
import { useUserAccessAndInvitations } from 'src/Libs/installation-rights-management/hooks'
import { InstallerWithAccessData } from 'src/Libs/retrofit'
import { getCityWithZip } from 'src/Libs/utils/string-functions'
import AddressBlock from '../AddressBlock'
import { getCompactTableSx, tableCellButtonSx } from '../table-styles'

type InstallersWithAccessContentProps = {
  installers: InstallerWithAccessData[]
  installationId: string | undefined
  isInstallationDecommissioned: boolean
  refetchAttachedInstallers: () => void
}

type InstallerTableRow = InstallerWithAccessData & {
  remove: React.ReactNode
}

/**
 * Table listing the individual installer users that have access to an installation (Flow theme).
 *
 * Flow installers are individual (Hager) users rather than companies, so access is removed by the
 * installer's account-link ID directly instead of resolving a company account link first.
 * @param installers - installers with their access rights
 * @param installationId - installation id
 * @param isInstallationDecommissioned - whether the installation is decommissioned
 * @param refetchAttachedInstallers - function to refetch attached installers
 */
const InstallersWithAccessContent: React.FC<InstallersWithAccessContentProps> = ({
  installers,
  installationId,
  isInstallationDecommissioned,
  refetchAttachedInstallers,
}) => {
  const { t } = useCustomTranslation()
  const { openReasonDialog } = useModals()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()

  const { removeAccountFromAccess } = useUserAccessAndInvitations({
    onSuccess: () => {
      enqueueSuccessStack(t('installation.details.info.remove-access.success-message'))
      refetchAttachedInstallers()
    },
    onError: error => {
      const fetchError = error as AuthFetchError
      if (fetchError?.response?.data.errno === FetchErrorCodes.InstallationAdminCannotBeDeleted) {
        enqueueErrorStack(t('installation.details.info.remove-access.owner-error-message'))
        return
      }

      enqueueErrorStack(t('installation.details.info.remove-access.error-message'))
    },
  })

  const onRemoveInstaller = async (accountLinkId: number, reason: string): Promise<void> => {
    const numericInstallationId = Number(installationId)
    if (!Number.isSafeInteger(numericInstallationId) || numericInstallationId <= 0) {
      enqueueErrorStack(t('installation.details.info.remove-access.error-message'))
      return
    }

    try {
      await removeAccountFromAccess(numericInstallationId, accountLinkId, reason)
    } catch (error) {
      console.error('Failed to remove installer access', error)
    }
  }

  if (installers.length === 0) {
    return (
      <Box sx={{ m: 2 }}>
        <Typography variant={'body2'}>{t('installation.details.info.no-installers-with-access')}</Typography>
      </Box>
    )
  }

  const columns: Header<InstallerTableRow>[] = [
    {
      value: 'name',
      label: t('common.name'),
      isSortable: false,
      valueRender: value => value || '-',
    },
    {
      value: 'accountId',
      label: t('common.id'),
      valueRender: value => value ?? '-',
      isSortable: false,
    },
    {
      value: 'address',
      label: t('common.address'),
      valueRender: (_, __, installer) => <InstallerAddress installer={installer} />,
      isSortable: false,
    },
    {
      value: 'email',
      label: t('common.email'),
      valueRender: value => value || '-',
      isSortable: false,
    },
    {
      value: 'phone',
      label: t('common.phone'),
      valueRender: value => value || '-',
      isSortable: false,
    },

    {
      value: 'startAt',
      label: t('installation.details.info.owner-start-access-at'),
      valueRender: value => (value ? format(new Date(value), t('installation.details.info.owner-start-access-at-format')) : '-'),
      isSortable: false,
    },
    {
      value: 'hasReadAccess',
      label: t('installation.access-and-invitations.table.header.columns.can-read'),
      valueRender: value => <RenderAccessIcon hasAccess={value} />,
      isSortable: false,
    },

    {
      value: 'hasWriteAccess',
      label: t('installation.access-and-invitations.table.header.columns.can-write'),
      valueRender: value => <RenderAccessIcon hasAccess={value} />,
      isSortable: false,
    },
    {
      value: 'remove',
      label: t('installation.details.info.remove-access.button-title'),
      valueRender: value => value,
      isSortable: false,
    },
  ]

  const tableData: InstallerTableRow[] = installers.map(installer => ({
    ...installer,
    remove: isInstallationDecommissioned ? null : (
      <Box sx={tableCellButtonSx}>
        <Button
          variant="text"
          onClick={() =>
            openReasonDialog({
              title: t('installation.details.info.remove-access.dialog-title'),
              description: t('installation.details.info.remove-installer-access.dialog-text', {
                installerName: installer.name,
              }),
              onSubmit: values => onRemoveInstaller(installer.accountLinkId, values.reason),
            })
          }
        >
          <Typography variant="overline">{t('installation.details.info.remove-access.button-title')}</Typography>
        </Button>
      </Box>
    ),
  }))

  return (
    <Box sx={getCompactTableSx()}>
      <TableComplete
        header={columns}
        data={tableData}
        headerElevation={0}
        bodyElevation={0}
        footerElevation={0}
        disablePagination
        allowOtherCellColors
      />
    </Box>
  )
}

// #region InstallerAddress
type InstallerAddressProps = {
  installer: InstallerWithAccessData | undefined
}

const InstallerAddress: React.FC<InstallerAddressProps> = ({ installer }) => {
  const { t } = useCustomTranslation()

  return (
    <AddressBlock
      street={installer?.street || undefined}
      city={getCityWithZip(installer?.zip || undefined, installer?.address || undefined)}
      country={installer?.countryCode ? t(`common.countries.short.${installer.countryCode}`) : undefined}
    />
  )
}
// #endregion

// #region RenderAccessIcon
type RenderAccessIconProps = {
  hasAccess: boolean
}

const RenderAccessIcon: React.FC<RenderAccessIconProps> = ({ hasAccess }) => {
  return <Box sx={{ display: 'flex' }}>{hasAccess ? <J2Validate sx={{ color: 'success.main' }} /> : <J2Multiply sx={{ color: 'error.main' }} />}</Box>
}
// #endregion

export default InstallersWithAccessContent
